// Read-only view of the Follower Tracker's summary (feature `followers`, owned by another engineer).
// AnalyticsPage queries GET /api/followers/summary with the tracker's own query key + fetcher, so it
// shares one request with the embedded <FollowersSummaryCard>; this module narrows the response to
// what analytics uses (KPI tile + daily follower counts). Parsed defensively: fields may be missing,
// and an error or empty summary means "fall back to analytics' own account_snapshots series".

import type { FollowerPoint } from "@gmx/shared/features/analytics";

export interface FollowersDay {
  day: string;
  followerCount: number | null;
  gained: number | null;
  lost: number | null;
  lostEstimated?: boolean;
}

export interface FollowersSummaryLite {
  current: number | null;
  gained: number | null;
  lost: number | null;
  net: number | null;
  lostEstimated: boolean;
  churnRate: number | null;
  series: FollowersDay[];
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

export function parseFollowersSummary(raw: unknown): FollowersSummaryLite | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const series = Array.isArray(r.series)
    ? r.series
        .filter((d): d is Record<string, unknown> => !!d && typeof d === "object" && typeof (d as { day?: unknown }).day === "string")
        .map((d) => ({ day: d.day as string, followerCount: num(d.followerCount), gained: num(d.gained), lost: num(d.lost), lostEstimated: d.lostEstimated === true }))
    : [];
  const out: FollowersSummaryLite = {
    current: num(r.current),
    gained: num(r.gained),
    lost: num(r.lost),
    net: num(r.net),
    lostEstimated: r.lostEstimated === true,
    churnRate: num(r.churnRate),
    series,
  };
  const empty = out.current === null && out.gained === null && out.lost === null && series.length === 0;
  return empty ? null : out;
}

/** Both APIs use the same observations; keep overview values together and fill missing days only. */
export function mergeFollowerSeries(own: FollowerPoint[], theirs: FollowersDay[] | undefined): FollowerPoint[] {
  if (!theirs?.length) return own;
  const byDay = new Map(theirs.filter((d) => d.followerCount !== null).map((d) => [d.day, d.followerCount as number]));
  return own.map((p) => ({ day: p.day, followers: p.followers ?? byDay.get(p.day) ?? null }));
}
